import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import type {
  Order,
  OrderList,
  OrderListQuery,
  OrderProofInput,
  OrderStatus,
  PurchaseList,
  PurchaseSummary,
  PurchasesAdminQuery,
} from "@nh/shared";
import {
  createOrderStripeLink,
  getAdminOrder,
  getPurchasesSummary,
  listAdminOrders,
  listAdminPurchases,
  updateOrderStatus,
  uploadOrderProof,
  type OrderDetail,
  type OrderStatusResult,
} from "./orders.api";

/**
 * Las llaves de caché, en un solo sitio, como en el resto del panel.
 * Compras cuelga de aquí porque comparte módulo y contrato con órdenes.
 */
export const adminOrderKeys = {
  allOrders: ["admin", "orders"] as const,
  allPurchases: ["admin", "purchases"] as const,
  orders: (query: OrderListQuery) =>
    [...adminOrderKeys.allOrders, "list", query] as const,
  one: (id: string) => [...adminOrderKeys.allOrders, "one", id] as const,
  purchases: (query: PurchasesAdminQuery) =>
    [...adminOrderKeys.allPurchases, "list", query] as const,
};

export function useAdminOrders(query: OrderListQuery): UseQueryResult<OrderList, Error> {
  return useQuery({
    queryKey: adminOrderKeys.orders(query),
    queryFn: () => listAdminOrders(query),
    // Al cambiar de página, conservar la anterior mientras llega la
    // nueva: la tabla no salta a esqueleto.
    placeholderData: (previous) => previous,
  });
}

export function useAdminOrder(
  id: string | undefined,
): UseQueryResult<OrderDetail, Error> {
  return useQuery({
    queryKey: adminOrderKeys.one(id ?? ""),
    queryFn: () => getAdminOrder(id as string),
    enabled: Boolean(id),
  });
}

export function useAdminPurchases(
  query: PurchasesAdminQuery,
): UseQueryResult<PurchaseList, Error> {
  return useQuery({
    queryKey: adminOrderKeys.purchases(query),
    queryFn: () => listAdminPurchases(query),
    placeholderData: (previous) => previous,
  });
}

export function usePurchasesSummary(
  query: PurchasesAdminQuery,
): UseQueryResult<PurchaseSummary, Error> {
  return useQuery({
    queryKey: ["admin", "purchases", "summary", query] as const,
    queryFn: () => getPurchasesSummary(query),
    placeholderData: (previous) => previous,
  });
}

export function useUploadOrderProof(
  id: string,
): UseMutationResult<Order, Error, OrderProofInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: OrderProofInput) => uploadOrderProof(id, input),
    onSuccess: (order) => {
      // La caché del detalle guarda { order, purchases }: se conserva lo
      // que ya se sabía de las compras.
      queryClient.setQueryData<OrderDetail>(adminOrderKeys.one(id), (prev) => ({
        order,
        purchases: prev?.purchases ?? [],
        commissions: prev?.commissions ?? [],
      }));
      void queryClient.invalidateQueries({ queryKey: adminOrderKeys.allOrders });
    },
  });
}

export function useCreateOrderStripeLink(id: string): UseMutationResult<Order, Error, void> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => createOrderStripeLink(id),
    onSuccess: (order) => {
      queryClient.setQueryData<OrderDetail>(adminOrderKeys.one(id), (prev) => ({
        order,
        purchases: prev?.purchases ?? [],
        commissions: prev?.commissions ?? [],
      }));
    },
  });
}

export function useUpdateOrderStatus(
  id: string,
): UseMutationResult<OrderStatusResult, Error, OrderStatus> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (status: OrderStatus) => updateOrderStatus(id, status),
    onSuccess: (result) => {
      queryClient.setQueryData<OrderDetail>(adminOrderKeys.one(id), (prev) => ({
        order: result.order,
        // Las compras recién creadas entran al detalle al instante.
        purchases: result.createdPurchases.length
          ? result.createdPurchases
          : (prev?.purchases ?? []),
        commissions: prev?.commissions ?? [],
      }));
      /*
       * Y el detalle se REFRESCA: la validación pudo registrar
       * comisiones, y esas solo llegan del servidor.
       */
      void queryClient.invalidateQueries({ queryKey: adminOrderKeys.one(id) });
      void queryClient.invalidateQueries({ queryKey: adminOrderKeys.allOrders });
      /*
       * El cambio pudo haber CREADO compras: la pantalla de compras
       * tiene que enterarse aunque esté abierta en otra pestaña del
       * panel.
       */
      void queryClient.invalidateQueries({ queryKey: adminOrderKeys.allPurchases });
    },
  });
}
